// src/pages/ShiftPage.jsx - 优化版本（限制高度，内部滚动）
import React, { useState, useEffect } from 'react';
import { Clock, DollarSign, ShoppingBag, TrendingUp, PlayCircle, StopCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { shiftApi } from '../api';
import ShiftStartDialog from '../components/shift/ShiftStartDialog';
import ShiftEndDialog from '../components/shift/ShiftEndDialog';
import ShiftHistoryTable from '../components/shift/ShiftHistoryTable';

const ShiftPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];
  
  const [currentShift, setCurrentShift] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);

  useEffect(() => {
    loadCurrentShift();
    loadHistory();
  }, []);

  const loadCurrentShift = async () => {
    try {
      const res = await shiftApi.getCurrent();
      if (res.success && res.data) {
        setCurrentShift(res.data);
      }
    } catch (err) {
      console.error('获取当前班次失败:', err);
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await shiftApi.getHistory({ limit: 10 });
      if (res.success) {
        setHistory(res.data || []);
      }
    } catch (err) {
      console.error('获取班次历史失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartShift = async (data) => {
    try {
      const res = await shiftApi.start(data);
      if (res.success) {
        alert(
          language === 'en' ? 'Shift started successfully' :
          language === 'km' ? 'ចាប់ផ្តើមវេនបានជោគជ័យ' :
          '班次开始成功'
        );
        setStartDialogOpen(false);
        loadCurrentShift();
      }
    } catch (err) {
      console.error('开始班次失败:', err);
      alert(
        language === 'en' ? 'Failed to start shift' :
        language === 'km' ? 'បរាជ័យក្នុងការចាប់ផ្តើមវេន' :
        '开始班次失败'
      );
    }
  };

  const handleEndShift = async (data) => {
    try {
      const res = await shiftApi.end(data);
      if (res.success) {
        alert(
          language === 'en' ? 'Shift ended successfully' :
          language === 'km' ? 'បញ្ចប់វេនបានជោគជ័យ' :
          '班次结束成功'
        );
        setEndDialogOpen(false);
        setCurrentShift(null);
        loadHistory();
      }
    } catch (err) {
      console.error('结束班次失败:', err);
      alert(
        language === 'en' ? 'Failed to end shift' :
        language === 'km' ? 'បរាជ័យក្នុងការបញ្ចប់វេន' :
        '结束班次失败'
      );
    }
  };

  const formatDuration = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = now - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}${language === 'en' ? 'h' : language === 'km' ? 'ម៉ោង' : '小时'} ${minutes}${language === 'en' ? 'm' : language === 'km' ? 'នាទី' : '分钟'}`;
  };

  return (
    <div className="h-full flex flex-col gap-6">
      {/* 头部 - 固定 */}
      <div className="flex-shrink-0">
        <h2 className="text-2xl font-bold">
          {t.shift || (language === 'en' ? 'Shift Management' : language === 'km' ? 'គ្រប់គ្រងវេន' : '班次管理')}
        </h2>
        <p className="text-gray-600 mt-1">
          {language === 'en' ? 'Manage your work shifts and cash register' :
           language === 'km' ? 'គ្រប់គ្រងវេនការងារនិងទឹកប្រាក់របស់អ្នក' :
           '管理您的工作班次和收银台'}
        </p>
      </div>

      {/* 当前班次状态 - 固定 */}
      <div className="flex-shrink-0">
        {currentShift ? (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-6 h-6" />
                  <h3 className="text-xl font-bold">
                    {language === 'en' ? 'Current Shift' :
                     language === 'km' ? 'វេនបច្ចុប្បន្ន' :
                     '当前班次'}
                  </h3>
                </div>
                <p className="text-blue-100">
                  {language === 'en' ? 'Cashier' : language === 'km' ? 'អ្នកគិតលុយ' : '收银员'}: {currentShift.cashier_name}
                </p>
                <p className="text-blue-100">
                  {language === 'en' ? 'Start Time' : language === 'km' ? 'ម៉ោងចាប់ផ្តើម' : '开始时间'}: {new Date(currentShift.start_time).toLocaleString(language === 'km' ? 'km-KH' : language)}
                </p>
                <p className="text-blue-100">
                  {language === 'en' ? 'Duration' : language === 'km' ? 'រយៈពេល' : '时长'}: {formatDuration(currentShift.start_time)}
                </p>
              </div>
              <button
                onClick={() => setEndDialogOpen(true)}
                className="flex items-center space-x-2 bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors font-medium"
              >
                <StopCircle className="w-5 h-5" />
                <span>
                  {language === 'en' ? 'End Shift' :
                   language === 'km' ? 'បញ្ចប់វេន' :
                   '结束班次'}
                </span>
              </button>
            </div>

            {/* 班次统计 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">
                      {language === 'en' ? 'Opening Cash' :
                       language === 'km' ? 'ទឹកប្រាក់បើក' :
                       '期初金额'}
                    </p>
                    <p className="text-2xl font-bold mt-1">
                      ${parseFloat(currentShift.opening_cash || 0).toFixed(2)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-200" />
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">
                      {language === 'en' ? 'Total Sales' :
                       language === 'km' ? 'ការលក់សរុប' :
                       '销售总额'}
                    </p>
                    <p className="text-2xl font-bold mt-1">
                      ${parseFloat(currentShift.total_sales || 0).toFixed(2)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-200" />
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">
                      {language === 'en' ? 'Transactions' :
                       language === 'km' ? 'ប្រតិបត្តិការ' :
                       '交易数量'}
                    </p>
                    <p className="text-2xl font-bold mt-1">
                      {currentShift.transaction_count || 0}
                    </p>
                  </div>
                  <ShoppingBag className="w-8 h-8 text-blue-200" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
            <Clock className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {language === 'en' ? 'No Active Shift' :
               language === 'km' ? 'គ្មានវេនសកម្ម' :
               '没有活动班次'}
            </h3>
            <p className="text-gray-500 mb-6">
              {language === 'en' ? 'Start a new shift to begin working' :
               language === 'km' ? 'ចាប់ផ្តើមវេនថ្មីដើម្បីចាប់ផ្តើមធ្វើការ' :
               '开始新班次以开始工作'}
            </p>
            <button
              onClick={() => setStartDialogOpen(true)}
              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <PlayCircle className="w-5 h-5" />
              <span>
                {language === 'en' ? 'Start Shift' :
                 language === 'km' ? 'ចាប់ផ្តើមវេន' :
                 '开始班次'}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* 班次历史 - 可滚动区域 */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow flex flex-col">
        <div className="flex-shrink-0 p-6 border-b">
          <h3 className="text-lg font-bold">
            {language === 'en' ? 'Shift History' :
             language === 'km' ? 'ប្រវត្តិវេន' :
             '班次历史'}
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <ShiftHistoryTable shifts={history} />
          )}
        </div>
      </div>

      {/* 对话框 */}
      {startDialogOpen && (
        <ShiftStartDialog
          onClose={() => setStartDialogOpen(false)}
          onSubmit={handleStartShift}
        />
      )}

      {endDialogOpen && currentShift && (
        <ShiftEndDialog
          shift={currentShift}
          onClose={() => setEndDialogOpen(false)}
          onSubmit={handleEndShift}
        />
      )}
    </div>
  );
};

export default ShiftPage;